import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigationType, useParams } from 'react-router';
import { Link, useSearchParams } from 'react-router-dom';
import { coinApi } from '../../api/coin';
import { resetSelectedAsset } from '../../store/reducers/app';
import { selectFilterOption } from '../../store/reducers/coin';
import {
  bitcoinPrice,
  dailyVolume,
  getHistoryVolume,
} from '../../store/reducers/price';
import { supportedBlockchains } from '../../utils/supportedBlockchains';
import './Asset.scss';
import AssetMarket from './AssetMarket';
import AssetTitle from './AssetTitle';
import DappUsage from './DappUsage';
import DevActivity from './DevActivity';
import Exchanges from './Exchanges';
import Filters from './Filters';
import Holders from './Holders';
import InvestorProfile from './InvestorProfile';
import InvestPortfolio from './InvestPortfolio';

const DEFAULT_TAB = 'Audience Profile';

const Asset = () => {
  const dispatch = useDispatch();
  const { name } = useParams();
  const { pathname, search } = useLocation();
  const navType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(null);
  const [days, setDays] = useState('30');
  const [isSupportBlockchain, setIsSupportBlockchain] = useState(null);
  const [pages, setPages] = useState([]);
  const [networks, setNetworks] = useState([]);

  const filterOption = useSelector(selectFilterOption);
  const historyVolume = useSelector(getHistoryVolume);
  const getPage = useCallback(
    (pageLabel) => pages.find((page) => page.label === pageLabel),
    [pages]
  );

  const {
    currentData: asset,
    isLoading,
    isFetching,
  } = coinApi.useGetCoinByIdQuery(name);

  const getIsOrdinalsOrFlow = useCallback(() => {
    if (!asset) return false;
    const chains = Object.keys(asset?.platforms);
    return chains.includes('ordinals') || chains.includes('flow');
  }, [asset]);

  const isOrdinalsOrFlow = getIsOrdinalsOrFlow();

  const {
    currentData: audienceProfileData,
    isLoading: isAudienceProfileLoading,
    isFetching: isAudienceProfileFetching,
    isSuccess: isSuccessAudienceProfile,
    refetch,
  } = coinApi.useGetTokenAudienceProfileQuery(
    {
      id: pathname.split('/')[2],
      audience: filterOption,
    },
    {
      skip: !pathname?.includes('coin'),
    }
  );

  const { data: bitcoin } = coinApi.useGetCoinFromMarketDataQuery({
    id: 'bitcoin',
    days,
  });

  const {
    data: holders,
    isLoading: isLoadingHolders,
    isFetching: isFetchingHolders,
  } = coinApi.useGetCoinHoldersQuery(name);

  useEffect(() => {
    if (isSuccessAudienceProfile) {
      refetch();
    }
  }, [refetch, filterOption]);

  useEffect(() => {
    const supportedChains = supportedBlockchains(asset?.platforms);
    if (asset && supportedChains.length === 1 && !isLoading && !isFetching) {
      const networksArr = supportedChains.map((platform) => platform);
      setNetworks(networksArr);
    }
  }, [asset, isLoading, isFetching]);

  useEffect(() => {
    if (days === '30' && historyVolume) {
      dispatch(dailyVolume(historyVolume));
    }
  }, [days, dispatch, historyVolume]);

  useEffect(() => {
    if (bitcoin) {
      dispatch(bitcoinPrice(bitcoin));
    }
  }, [bitcoin, dispatch]);

  useEffect(() => {
    const tabName = searchParams.get('tab')?.replace('+', ' ') || DEFAULT_TAB;
    const holdersTabs = ['current', 'relevant'];
    const isHoldersTab = tabName === 'Holders';
    const shouldSwitchToHolders =
      holdersTabs.includes(filterOption) && tabName === 'List';
    const shouldSwitchToList =
      !holdersTabs.includes(filterOption) && isHoldersTab;
    if (shouldSwitchToHolders || shouldSwitchToList) {
      const switchLabel = shouldSwitchToHolders ? 'Holders' : 'List';
      setSelectedTab(getPage(switchLabel));
      setSearchParams({
        ...Object.fromEntries([...searchParams]),
        page: '1',
        tab: switchLabel,
      });
    }
    setSelectedTab(pages.length ? getPage(tabName) : null);
  }, [searchParams, pages, filterOption]);

  useEffect(() => {
    if (isLoading || isFetching) {
      setIsSupportBlockchain(null);
    }
    if (asset) {
      const platformsSupporting =
        supportedBlockchains(asset.platforms).length > 0;

      setIsSupportBlockchain(platformsSupporting);

      setPages([
        {
          label: 'Audience Profile',
          component: (
            <InvestorProfile
              holders={audienceProfileData?.wallet_count}
              isHoldersLoading={
                isAudienceProfileLoading || isAudienceProfileFetching
              }
              symbol={asset.symbol}
              isSupportedBlockchain={platformsSupporting}
              audienceProfileData={audienceProfileData}
              isAudienceProfileLoading={
                isAudienceProfileLoading || isAudienceProfileFetching
              }
              networks={networks}
            />
          ),
          id: 1,
        },
        {
          label: 'Audience Portfolio',
          component: (
            <InvestPortfolio
              holders={audienceProfileData?.wallet_count}
              isSupportedBlockchain={platformsSupporting}
            />
          ),
          id: 2,
        },
        {
          label: ['current', 'relevant'].includes(filterOption)
            ? 'Holders'
            : 'List',
          component: <Holders isSupportedBlockchain={platformsSupporting} />,
          id: 3,
        },
        ...(!isOrdinalsOrFlow
          ? [
              {
                label: 'Dapp Usage',
                component: (
                  <DappUsage
                    holders={audienceProfileData?.wallet_count}
                    isSupportedBlockchain={platformsSupporting}
                  />
                ),
                id: 4,
              },
            ]
          : []),
        ...(!isOrdinalsOrFlow
          ? [
              {
                label: 'Centralized Exchanges',
                component: (
                  <Exchanges isSupportedBlockchain={platformsSupporting} />
                ),
                id: 5,
              },
            ]
          : []),
        {
          label: 'Developer Activity',
          component: (
            <DevActivity holders={audienceProfileData?.wallet_count} />
          ),
          id: 6,
        },
        // { label: 'Markets', component: <ComingSoon />, id: 4 },
        // { label: 'News', component: <ComingSoon />, id: 5 },
        // { label: 'Social', component: <ComingSoon />, id: 6 },
        // { label: 'Contract', component: <ComingSoon />, id: 7 },
        // { label: 'Competitors', component: <ComingSoon />, id: 8 },
      ]);
    }
  }, [
    asset,
    holders,
    isFetchingHolders,
    isLoading,
    isFetching,
    isLoadingHolders,
    audienceProfileData,
    isAudienceProfileLoading,
    isAudienceProfileFetching,
    networks,
    filterOption,
    isOrdinalsOrFlow,
  ]);

  useEffect(() => {
    if (searchParams.get('scroll') && navType === 'POP') {
      setTimeout(
        () =>
          window.scrollTo({
            top: +searchParams.get('scroll'),
            left: 0,
            behavior: 'smooth',
          }),
        100
      );
    }
  }, [searchParams.get('scroll')]);

  useEffect(
    () => () => {
      dispatch(resetSelectedAsset());
      setIsSupportBlockchain(null);
    },
    [dispatch]
  );

  const tabContent = useMemo(() => {
    if (typeof isSupportBlockchain === 'boolean') {
      return (
        <>
          <Filters />
          <div className="asset-section m-o mt-3">
            <div className="title-gap asset-navigation">
              <ul className="nav nav-pills gap-2">
                {pages.map((elem) => (
                  <li
                    role="presentation"
                    className="nav-item cursor-pointer"
                    key={elem.id}
                    onClick={() => {
                      setSelectedTab(getPage(elem.label));
                      setSearchParams({
                        ...Object.fromEntries([...searchParams]),
                        tab: elem.label,
                        scroll: '',
                        page: '1',
                        orderBy: '',
                      });
                    }}
                  >
                    <Link
                      className={`nav-link ${elem.label === selectedTab?.label ? 'active' : ''}`}
                      to={`${pathname}${search}`}
                    >
                      {elem.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="dropdown-divider w-100" />
            {selectedTab?.component}
          </div>
        </>
      );
    }
    return null;
  }, [
    isSupportBlockchain,
    pages,
    selectedTab?.component,
    selectedTab?.label,
    pathname,
    search,
    getPage,
    setSearchParams,
    searchParams,
  ]);

  return (
    <>
      {!(isLoading || isFetching) && (
        <Helmet>
          <meta charSet="utf-8" />
          <title>{asset.name} Insights - Absolute Labs Platform</title>
        </Helmet>
      )}
      <AssetTitle asset={asset} isLoading={isLoading || isFetching} />
      <div className="asset-section mt-3">
        <AssetMarket
          asset={asset}
          isAssetLoading={isLoading || isFetching}
          holders={holders}
          isHoldersLoading={isLoadingHolders || isFetchingHolders}
          days={days}
          setDays={setDays}
          networks={networks}
        />
      </div>
      {tabContent}
    </>
  );
};

export default Asset;
